import React, {useEffect, useState} from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  SnackbarCloseReason,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {visuallyHidden} from '@mui/utils';
import {t} from 'i18next';

import AurButton from '../common/aur-button/AurButton';
import {
  donutColor,
  formatBudgetValue,
  getAlertHitValue,
  getUTCDateTimeForDisplay,
} from '../../utils/utils';
import AurIcon, {IconNames} from '../common/aur-icon/AurIcon';
import {useDispatch, useSelector} from 'react-redux';
import {
  setBudgetAlertCount,
  setBudgetAlertId,
  setShowBudgetTrackerAlertModal,
  setShowBudgetTrackerDeleteToast,
  setShowBudgetTrackerModal,
  setViewProjectName,
  setViewProjectPageInfo,
} from '../../redux/slices/budgetTrackerSlice';
import styles from './../common/aur-select-dropdown/style.module.scss';
import {
  getBudgetTrackerAlert,
  removeBudgetTrackerAlert,
} from '../../api/SelectiveSelfManagementService';
import {IBudgetTrackerResponse} from './model';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import {RootState} from '../../redux/store';
import {setModalDropDownSelectedItem} from '../../redux/slices/invoiceModalSlice';
import AurToast from '../common/aur-toast/AurToast';
import {AxiosError} from 'axios';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {
  BUDGET_CONSUMPTON_PERCENTAGE,
  ONLY_DATE_SHOW_FORMAT,
} from '../../constants/constants';
import { budgetViewProjects } from '../../api/ClientManagementService';
import {AurLightTooltip} from '../common/aur-light-tooltip/aur-light-tooltip';

const BudgetTrackerAlertModal = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isBudgetTrackerOpen, setBudgetTrackerOpen] = useState(true);
  const {showToast} = useToast();
  const SCROLL_BAR_STYLE_CLASS = 'aur-item-container-scroll';
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [budgetAlertList, setBudgetAlertList] = useState<
    IBudgetTrackerResponse['alert'][] | null
  >([]);
  const dispatch = useDispatch();
  const clientId = useSelector((state: RootState) => state.reports.clientId);

  const showBudgetTrackeDeleteToast = useSelector(
    (state: RootState) => state.budgetTrackerModal
  ).showBudgetTrackerDeleteToast;

  const fetchBudgetTrackerAlert = async () => {
    setIsLoading(true);
    try {
      const response = await getBudgetTrackerAlert(clientId as string);
      const budgetTrackerData = response?.data?.data;
      if (budgetTrackerData?.length == 0) {
        handleCloseModal();
      }
      const bugetTrackerOrder = budgetTrackerData?.sort((a: any, b: any) => {
        return b.budgetConsumption - a.budgetConsumption;
      });
      setBudgetAlertList(bugetTrackerOrder);
    } catch (error) {
      console.error('Error fetching overlays:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const budgetItemsPerPage = useSelector(
    (state: RootState) => state.projectOverview
  ).itemsPerPage;

  useEffect(() => {
    fetchBudgetTrackerAlert();
  }, []);

  const handleCloseModal = () => {
    setBudgetTrackerOpen(false);
    dispatch(setShowBudgetTrackerAlertModal(false));
  };
  const handleRemoveAlert = async (budgetAlertId: number) => {
    setIsLoading(true);
    try {
      await removeBudgetTrackerAlert(budgetAlertId);
      dispatch(setShowBudgetTrackerDeleteToast(true));
      fetchBudgetTrackerAlert();
    } catch (error) {
      const err = error as AxiosError;
      showToast(err.message, 'error', undefined, 'center');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(setShowBudgetTrackerDeleteToast(false));
  };

  useEffect(() => {
    if (showBudgetTrackeDeleteToast) {
      setOpenToast(true);
      setTimeout(() => {
        setOpenToast(true);
      }, 5000);

      setInterval(() => {
        setOpenToast(false);
        dispatch(dispatch(setShowBudgetTrackerDeleteToast(false)));
      }, 5000);
    }
  }, [showBudgetTrackeDeleteToast]);

  const redirectToBudgetDetails = (
    projectId: string,
    projectName: string,
    budgetAlertId: number
  ) => {
    dispatch(setBudgetAlertId(budgetAlertId));
    dispatch(setBudgetAlertCount(budgetAlertList?.length ?? 0));
    dispatch(setShowBudgetTrackerModal(true));
    dispatch(
      setModalDropDownSelectedItem({
        id: projectId,
        name: projectName,
      })
    );
    dispatch(setShowBudgetTrackerAlertModal(false));
  };

  const renderModalHeader = () => (
    <Box className="budget-title-section">
      <Typography id="budget-modal-title" className="typo-text headline3_size">
        {` ${budgetAlertList?.length} ${t('ecaHomepageModals.budgetTracker.BudgetTrackerAlert.title')}`}
      </Typography>
      <Box>
        <span className="typo-text body4_size current-data-view" tabIndex={0}>
          {t(
            'ecaHomepageModals.budgetTracker.budgetTrackerOverview.dataCurrentAsOf'
          )}
          &nbsp;
        </span>
        <AurLightTooltip title={t('common.yearMonthDate')}>
          <span className="typo-text label2_size budget-view-text" tabIndex={0}>
            {getUTCDateTimeForDisplay(new Date())}
          </span>
        </AurLightTooltip>
      </Box>
    </Box>
  );
  
  const callViewProjectBtn = async(clientId:string, projectId:string, projectName:string) => {
     try {
      const params = `?projectId=${projectId}&clientId=${clientId}&pageSize=${budgetItemsPerPage}`
      const response = await budgetViewProjects(params);
      const clientPageInfo = response?.data?.data;
      dispatch(setViewProjectPageInfo(clientPageInfo)) 
    } catch (error) {
      console.log("Error ::", error);
    } finally {
      dispatch(setViewProjectName(projectName));
      handleCloseModal();
    }
  }

  const redirectToProject = (e: any, alert: any) => {
    e.stopPropagation();
    callViewProjectBtn(alert.clientId, alert.projectId, alert.projectName)
  };
  const BudgetTrackerGridView = () => {
    return (
      <Box
        className={`${SCROLL_BAR_STYLE_CLASS} ${styles.aurItemContainerScrollbar} budget-alert-list-container`}
      >
        {budgetAlertList?.length != 0 &&
          budgetAlertList?.map((alert: any) => {
            const budetConsumptionPercentage = alert?.budgetConsumption * BUDGET_CONSUMPTON_PERCENTAGE;
            const color = donutColor(budetConsumptionPercentage);
            const hitValue = getAlertHitValue(budetConsumptionPercentage);
            return (
              <Box className="budget-alert-list">
                <Box className="alert-chart-container">
                  <Typography
                    className="typo-text subhead2_size"
                    style={{color: color}}
                    tabIndex={0}
                    aria-label={`${formatBudgetValue(budetConsumptionPercentage)} ${t('ecaHomepageModals.budgetTracker.BudgetTrackerAlert.estimatedBudgetUsed')}`}
                  >
                    {`${formatBudgetValue(budetConsumptionPercentage)}${t('ecaHomepageModals.budgetTracker.BudgetTrackerAlert.estBudgetUsed')}`}
                  </Typography>
                  <AurLightTooltip title={t('common.yearMonthDate')}>
                    <Typography
                      className="typo-text body4_size budget-alert-hit"
                      tabIndex={0}
                    >
                      {`Hit ${hitValue}% on ${getUTCDateTimeForDisplay(alert?.alertGeneratedOn, ONLY_DATE_SHOW_FORMAT)}`}
                    </Typography>
                  </AurLightTooltip>

                  <Box className="budget-alert-action">
                    <AurButton
                      classNames="budget-view-details-btn"
                      type="continue"
                      size="Medium"
                      ariaLabel={t(
                        'ecaHomepageModals.budgetTracker.BudgetTrackerAlert.viewEstimatedLabel'
                      )}
                      label={t(
                        'ecaHomepageModals.budgetTracker.BudgetTrackerAlert.viewEstimatedBudget'
                      )}
                      dataTestId="view-estimated-button"
                      onClick={() =>
                        redirectToBudgetDetails(
                          alert.projectId,
                          alert.projectName,
                          alert.budgetAlertId
                        )
                      }
                    />
                    <AurButton
                      classNames="budget-view-project-btn"
                      type="secondary"
                      size="Medium"
                      ariaLabel={t(
                        'ecaHomepageModals.budgetTracker.BudgetTrackerAlert.viewProject'
                      )}
                      label={t(
                        'ecaHomepageModals.budgetTracker.BudgetTrackerAlert.viewProject'
                      )}
                      dataTestId="view-project"
                      onClick={(e) => redirectToProject(e, alert)}
                    />
                  </Box>
                </Box>
                <Box className="alert-chart-action">
                  <Box className="alert-chart-content">
                    <Box className="alert-content-name-action">
                      <Box className="alert-content-project">
                        <Typography
                          className="typo-text label2_size alert-project-name"
                          tabIndex={0}
                        >
                          {t(
                            'ecaHomepageModals.budgetTracker.budgetTrackerOverview.projectName'
                          )}
                        </Typography>
                        <Typography
                          className="typo-text subhead1_size"
                          tabIndex={0}
                        >
                          {alert?.projectName}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className="alert-content-code">
                      <Typography
                        className="typo-text label2_size alert-project-name"
                        tabIndex={0}
                      >
                        {t(
                          'ecaHomepageModals.budgetTracker.budgetTrackerOverview.engagementCode'
                        )}
                      </Typography>
                      <Typography
                        className="typo-text subhead3_size"
                        tabIndex={0}
                      >
                        {alert?.engagementCode}
                      </Typography>
                    </Box>
                  </Box>
                  <AurButton
                    type="secondary"
                    size="Small"
                    onClick={() =>
                      handleRemoveAlert(alert.budgetAlertId as number)
                    }
                    dataTestId="delete-button"
                    classNames="rule-action-icon-button"
                    ariaLabel={`Remove alert for ${alert?.projectName}`}
                  >
                    {' '}
                    <AurIcon icon={IconNames.Remove} />{' '}
                  </AurButton>
                </Box>
              </Box>
            );
          })}
      </Box>
    );
  };

  const renderDialogActions = () => (
    <DialogActions
      className="footer-action-container "
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: '16px 32px',
      }}
    >
      <Typography className="typo-text body4_size support-text">
        {t('ecaHomepageModals.budgetTracker.budgetTrackerOverview.supportText')}
      </Typography>

      <Stack direction="row" spacing={2}>
        <AurButton
          type="continue"
          size="Medium"
          onClick={handleCloseModal}
          label={t(
            'ecaHomepageModals.budgetTracker.budgetTrackerOverview.closeButton'
          )}
          classNames="budget-tracker-modal-close-button"
          dataTestId="budget-tracker-close-button"
          aria-describedby="close-button-desc"
        >
          <Typography id="close-button-desc" style={visuallyHidden}>
            {t(
              'ecaHomepageModals.budgetTracker.budgetTrackerOverview.closeButtonHint'
            )}
          </Typography>
        </AurButton>
      </Stack>
    </DialogActions>
  );

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isBudgetTrackerOpen}
      onClose={handleCloseModal}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: '#1F2B3D',
          },
        },
      }}
      PaperProps={{
        className: 'budget-tracker-container budget-tracker-alert-container',
        sx: {
          backgroundImage: 'none',
        },
      }}
      maxWidth={false}
      aria-labelledby="budget-modal-title"
      aria-describedby="budget-modal-description"
    >
      <div className="budget-alert-loader">
        <AurLoadingIndicator show={isLoading} />
      </div>
      <DialogContent className="model-content">
        {renderModalHeader()}

        {BudgetTrackerGridView()}
      </DialogContent>
      {renderDialogActions()}
      <AurToast
        ariaLabel={t('common.toastAlert.toastAlertHint')}
        hideDuration={5000}
        open={openToast}
        horizontalPosition={'center'}
        message={t(
          'ecaHomepageModals.budgetTracker.budgetTrackerOverview.deleteToastMessage'
        )}
        onClose={() => handleSnackbarClose}
      />
    </Dialog>
  );
};

export default BudgetTrackerAlertModal;
